import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, Leaf, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter email and password");
      return;
    }
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Admin");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/40 px-4 py-10" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="admin-back-home">
          <ArrowLeft className="w-4 h-4" /> Back to website
        </Link>
        <Card className="p-8 rounded-3xl border-border bg-white">
          <div className="text-center mb-6">
            <img src="/logo.png" alt="Indapur FPC" className="w-20 h-20 object-contain mx-auto mb-4" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Indapur FPC Admin</h1>
            <p className="text-sm text-muted-foreground">Sign in to manage submissions</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="mb-1.5 block">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin email" data-testid="admin-email" />
            </div>
            <div>
              <Label htmlFor="password" className="mb-1.5 block">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="admin-password" />
            </div>
            <Button type="submit" className="w-full rounded-full bg-primary hover:bg-primary/90" disabled={submitting} data-testid="admin-login-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              Sign In
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
